import { useMsal } from '@azure/msal-react';
import { Button, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useStores } from '../../common/hooks';
import { WinnerEventRequest } from '../../models/schemas';
import { ConfirmationDialog, PageTitle } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { AddOrEditWinnerEventModal } from './components/AddOrEditWinnerEventModal';
import { WinnerEventsTable } from './components/WinnerEventsTable';
import './WinnerRequirementPage.scss';

const WinnerRequirementPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    awardStore,
    winnerRequirementPageUiStore
  } = useStores();

  const getWinnerEvents = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    const winnerEvents = await apiStore.getWinnerEvents(instance, awardStore.defaultProgramRef.programId);
    winnerRequirementPageUiStore.setWinnerEvents(winnerEvents);
  }

  const handleAddWinnerEvent = async (winnerEvent: WinnerEventRequest): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    winnerRequirementPageUiStore.setLoading(true);
    try {
      await apiStore.createWinnerEvent(instance, awardStore.defaultProgramRef.programId, winnerEvent);
      await getWinnerEvents();
      winnerRequirementPageUiStore.setAddOrEditEventModalType(undefined);
    } catch (error) {
      // TODO - handle error
    } finally {
      winnerRequirementPageUiStore.setLoading(false);
    }
  }

  const handleUpdateWinnerEvent = async (winnerEvent: WinnerEventRequest): Promise<void> => {
    if (!awardStore.defaultProgramRef || !winnerRequirementPageUiStore.selectedWinnerEvent) return;
    winnerRequirementPageUiStore.setLoading(true);
    try {
      await apiStore.updateWinnerEvent(instance, awardStore.defaultProgramRef.programId, winnerRequirementPageUiStore.selectedWinnerEvent.eventId, winnerEvent);
      await getWinnerEvents();
      winnerRequirementPageUiStore.setAddOrEditEventModalType(undefined);
    } catch (error) {
      // TODO - handle error
    } finally {
      winnerRequirementPageUiStore.setLoading(false);
    }
  }

  const handleDeleteWinnerEvent = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef || !winnerRequirementPageUiStore.selectedWinnerEvent) return;
    winnerRequirementPageUiStore.setLoading(true);
    try {
      await apiStore.deleteWinnerEvent(instance, awardStore.defaultProgramRef.programId, winnerRequirementPageUiStore.selectedWinnerEvent.eventId);
      await getWinnerEvents();
      winnerRequirementPageUiStore.setShowDeleteEventModal(false);
    } catch (error) {
      // TODO - handle error
    } finally {
      winnerRequirementPageUiStore.setLoading(false);
    }
  }

  const preloadData = async (): Promise<void> => {
    winnerRequirementPageUiStore.setLoading(true);
    try {
      await getWinnerEvents();
    } catch (error) {
      // TODO - handle error
    } finally {
      winnerRequirementPageUiStore.setLoading(false);
    }
  }

  const handleAddNewWinnerEvent = (): void => {
    winnerRequirementPageUiStore.createWinnerEventRequestBody();
    winnerRequirementPageUiStore.setAddOrEditEventModalType('add');
  };

  useEffect(() => {
    preloadData();
    return () => {
      winnerRequirementPageUiStore.resetStates();
    };
  }, []);

  return (
    <div className='winner-requirement-page'>
      <PageTitle
        title='Winner Requirements'
        subtitle={awardStore.defaultProgramRef?.displayName}
      />
      {
        winnerRequirementPageUiStore.addOrEditEventModalType !== undefined &&
        <AddOrEditWinnerEventModal
          isModalOpen
          onDismissModal={() => winnerRequirementPageUiStore.setAddOrEditEventModalType(undefined)}
          onAddWinnerEvent={handleAddWinnerEvent}
          onUpdateWinnerEvent={handleUpdateWinnerEvent}
        />
      }
      {
        winnerRequirementPageUiStore.isDeleteEventModalOpen &&
        <ConfirmationDialog
          isOpen
          dialogTitle='Delete award event'
          dialogSubtext={`Delete award event "${winnerRequirementPageUiStore.selectedWinnerEvent?.displayName}"?`}
          primaryButtonLabel='Delete'
          secondaryButtonLabel='Back'
          onPrimaryButtonClick={() => handleDeleteWinnerEvent()}
          onSecondaryButtonClick={() => winnerRequirementPageUiStore.setShowDeleteEventModal(false)}
          onCloseDialogClick={() => winnerRequirementPageUiStore.setShowDeleteEventModal(false)}
        />
      }
      {
        winnerRequirementPageUiStore.isLoading && <Loading />
      }
      <FlexBox
        content='space-between'
        className='winner-requirement-page__header'
      >
        <Text
          size={500}
          weight='semibold'
        >
          Award events
        </Text>
        <Button
          appearance='primary'
          onClick={handleAddNewWinnerEvent}
        >
          Add New
        </Button>
      </FlexBox>
      <WinnerEventsTable />
    </div>
  );
}

export const WinnerRequirementPage = observer(WinnerRequirementPageImpl);
